import * as React from 'react';
import './App.css';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import AddImage from './components/AddImage';
import BreakpointRow from './components/BreakpointRow';
import PixboostJsSnippet from './components/PixboostJsSnippet';
import Alert from 'reactstrap/lib/Alert';
import ReactIframeResizer from 'react-iframe-resizer-super';
import FontAwesome from 'react-fontawesome';
import Input from 'reactstrap/lib/Input';
import Label from 'reactstrap/lib/Label';
import FormGroup from 'reactstrap/lib/FormGroup';
import { ChangeEvent } from 'react';
import { Nav, Navbar, NavbarBrand, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import HTMLSnippet from './components/HTMLSnippet';
import * as _ from 'lodash';
import Help from './components/Help';

interface Breakpoint {
    name: string;
    displayName?: string;
    url: string;
    width?: number;
    height?: number;
    hide: boolean;
    asis: boolean;
    previewWidth: number;
    mediaQuery: string;
}

interface State {
    imageAdded: boolean;
    apiKey: string | null;
    breakpoints?: Breakpoint[];
    responsive: boolean;
    lazy: boolean;
    altText: string;
    snippet?: string;
    htmlSnippet?: string;
    previewWidth: number;
    activeBreakpoint: string;
    activeTab: string;
    backgroundImage: boolean;
}

interface Props {
    apiKey?: string;
}

function getParameterByName(name: string): string | null {
    if (typeof window !== 'undefined') {
        const url = window.location.href;
        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) {
            return null;
        }
        if (!results[2]) {
            return '';
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    } else {
        return null;
    }
}

const INITIAL_STATE = {
    imageAdded: false,
    previewWidth: 1000,
    activeBreakpoint: 'lg',
    responsive: true,
    lazy: true,
    altText: '',
    activeTab: 'pixboostJs',
    backgroundImage: false
};

class App extends React.Component<Props, State> {
    previewFrame: HTMLIFrameElement | null;
    previewHTMLFrame: HTMLIFrameElement | null;
    dispatchResizeEvent: () => void; // To override in unit tests
    dispatchPreviewUpdate: (doc: Document) => void; // To override in unit tests
    generatePreview: () => void;

    constructor(props: Props) {
        super(props);

        this.previewFrame = null;
        this.previewHTMLFrame = null;

        this.dispatchResizeEvent = () => {
            const ev = document.createEvent('Event');
            ev.initEvent('resize', true, true);
            window.dispatchEvent(ev);
        };
        this.dispatchPreviewUpdate = (doc: Document) => {
            const evt = new CustomEvent('updatePreview', {detail: {apiKey: this.state.apiKey}});
            // Need to debounce for resize event as it's updating width straight
            // @ts-ignore
            _.debounce(() => {
                doc.dispatchEvent(evt);
            },         500)();
        };
        this.generatePreview = _.debounce(this.doGeneratePreview, 1000);

        this.state = {
            ...INITIAL_STATE,
            apiKey: props.apiKey || getParameterByName('k')
        };
    }

    componentDidMount(): void {
        const imageUrl = getParameterByName('url');
        if (imageUrl) {
            this.onAddImage(imageUrl);
        }
    }

    onAddImage(imageUrl: string) {
        if (this.state.imageAdded) {
            const breakpoints = (this.state.breakpoints || []).map(b => {
                return {
                    ...b,
                    url: imageUrl
                };
            });
            this.setState({breakpoints});
        } else {
            this.setState({
              imageAdded: true,
              breakpoints: [
                  {
                      name: 'lg',
                      displayName: 'Desktop',
                      url: imageUrl,
                      hide: false,
                      asis: false,
                      previewWidth: 1000,
                      mediaQuery: '(min-width: 990px)'
                  },
                  {
                      name: 'md',
                      displayName: 'Tablet',
                      url: imageUrl,
                      hide: false,
                      asis: false,
                      previewWidth: 768,
                      mediaQuery: '(min-width: 640px)'
                  },
                  {
                      name: 'sm',
                      displayName: 'Mobile',
                      url: imageUrl,
                      hide: false,
                      asis: false,
                      previewWidth: 639,
                      mediaQuery: ''
                  }
              ]
            });
        }
    }

    onBreakpointChanged(breakpoint: Breakpoint) {
        if (this.state.breakpoints) {
            const newBreakpoints = [...this.state.breakpoints];
            newBreakpoints.forEach(b => {
                if (b.name === breakpoint.name) {
                    Object.assign(b, breakpoint);
                }
            });

            this.setState({
                breakpoints: newBreakpoints
            });
        }
    }

    onSnippetChanged(snippet: string) {
        if (snippet !== this.state.snippet) {
            this.setState(
                {
                    snippet: snippet
                },
                this.generatePreview
            );
        }
    }

    onHTMLSnippetChanged(snippet: string) {
        if (snippet !== this.state.htmlSnippet) {
            this.setState(
                {
                    htmlSnippet: snippet
                },
                this.generatePreview
            );
        }
    }

    loadPreview(doc: Document, snippet: string | undefined) {
        const preview = doc.getElementById('preview');
        if (preview) {
            preview.innerHTML = snippet || '';
            this.dispatchPreviewUpdate(doc);
        }
    }

    doGeneratePreview() {
        if (this.state.activeTab === 'pixboostJs' && this.previewFrame) {
            const doc = this.previewFrame.contentDocument as Document;
            if (doc.readyState === 'loading') {
                doc.addEventListener('DOMContentLoaded', () => {
                    this.loadPreview(doc, this.state.snippet);
                });
            } else {
                this.loadPreview(doc, this.state.snippet);
            }
        }
        if (this.state.activeTab === 'html' && this.previewHTMLFrame) {
            const doc = this.previewHTMLFrame.contentDocument as Document;
            if (doc.readyState === 'loading') {
                doc.addEventListener('DOMContentLoaded', () => {
                    this.loadPreview(doc, this.state.htmlSnippet);
                });
            } else {
                this.loadPreview(doc, this.state.htmlSnippet);
            }
        }
    }

    setActiveBreakpoint(name: string) {
        if (this.state.breakpoints) {
            const breakpoint = this.state.breakpoints.find((b: Breakpoint) => b.name === name);
            if (breakpoint) {
                this.changePreviewWidth(breakpoint.previewWidth);
                this.setState({
                    activeBreakpoint: name
                });
            }
        }
    }

    changePreviewWidth(width: number) {
        this.setState({previewWidth: width}, () => {
            this.dispatchResizeEvent();
            if (this.previewFrame) {
                const doc = this.previewFrame.contentDocument as Document;
                this.dispatchPreviewUpdate(doc);
            }
        });
    }

    breakpointIcon(name: string, id: string): JSX.Element {
        return (
            <FontAwesome
                name={name}
                size={'2x'}
                style={
                    {
                        color: `${this.state.activeBreakpoint === id ? '#007bff' : 'black'}`,
                        cursor: 'pointer',
                        verticalAlign: 'bottom'
                    }
                }
                onClick={() => this.setActiveBreakpoint(id)}
                className={`pr-2 js-bp-${id}`}
            />
        );
    }

    toggleTab(tab: string) {
        if (this.state.activeTab !== tab) {
            this.setState(
                {
                    activeTab: tab
                },
                this.generatePreview
            );
        }
    }

    render() {
        if (!this.state.apiKey) {
            return <Alert>Please, provide api key to start working with the application</Alert>;
        }

        const showPixboostJsPreview = this.state.activeTab === 'pixboostJs' ? 'block' : 'none';
        const showHTMLPreview = this.state.activeTab === 'html' ? 'block' : 'none';

        return (
            <div>
                <Navbar expand={'md'}>
                    <Container className="pb-2 pt-2">
                        <NavbarBrand tag={'div'}>
                            <a href="https://pixboost.com">
                                <span className={'logo-pix'}>
                                    pix
                                </span>
                                <span className={'logo-boost'}>boost</span>
                            </a>
                            . Snippet Generator
                        </NavbarBrand>
                    </Container>
                </Navbar>
                <Container className="pt-2 grey-panel">
                    <Row className={'pb-2 pt-2'}>
                        <Col md={12}>
                            <AddImage
                                onAddImage={(url) => this.onAddImage(url)}
                                onClear={() => {
                                    this.setState(INITIAL_STATE);
                                }}
                            />
                        </Col>
                    </Row>
                </Container>
                <Container className="pt-2">
                    {this.state.imageAdded && this.state.breakpoints &&
                    <div>
                        <div className="bottom-splitter">
                            <Row>
                                <Col md={2}>
                                    <FormGroup check={true}>
                                        <Label check={true} className={'col-form-label checkbox-with-help'}>
                                            <Input
                                                className="position-static js-responsive-mode mr-1"
                                                type="checkbox"
                                                name="one-image"
                                                checked={this.state.responsive}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    this.setState({responsive: e.target.checked});
                                                }}
                                            />{' '}
                                            Responsive
                                            <Help id={'help-responsive'}>
                                                Allows to set different sizes for different devices
                                            </Help>
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <FormGroup check={true}>
                                        <Label
                                            check={true}
                                            className={'col-form-label checkbox-with-help'}
                                        >
                                            <Input
                                                className="position-static js-lazy-load mr-1"
                                                type="checkbox"
                                                name="lazy-load"
                                                checked={this.state.lazy}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    this.setState({lazy: e.target.checked});
                                                }}
                                            />{' '}
                                            Lazy Load
                                            <Help id={'help-lazy'}>
                                                Image will appear after scrolling
                                            </Help>
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <FormGroup check={true}>
                                        <Label
                                            check={true}
                                            className={'col-form-label checkbox-with-help'}
                                        >
                                            <Input
                                                className="position-static js-background-image mr-1"
                                                type="checkbox"
                                                name="background-image"
                                                checked={this.state.backgroundImage}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    this.setState({backgroundImage: e.target.checked});
                                                }}
                                            />{' '}
                                            Background Image
                                            <Help id={'help-background-image'}>
                                                Using CSS background image
                                            </Help>
                                        </Label>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup row={true}>
                                        <Label md={2} for={'altText'}>Alt text</Label>
                                        <Col md={10}>
                                            <Input
                                                type={'text'}
                                                value={this.state.altText}
                                                id={'altText'}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                    this.setState({altText: e.target.value});
                                                }}
                                            />
                                        </Col>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                        <div className="pt-4 pb-2">
                            {
                                this.state.breakpoints.map((b, idx) => {
                                    return <BreakpointRow
                                        disabled={!this.state.responsive && idx !== 0}
                                        key={b.name}
                                        breakpoint={b}
                                        onBreakpointChange={bb => this.onBreakpointChanged(bb)}
                                        showHelp={idx === 0}
                                    />;
                                })
                            }
                        </div>

                        <div className="pt-2">
                            <h4>Preview</h4>
                            <div className="p-4 grey-panel">
                                <Row className="pb-1">
                                    <Col>
                                        {this.breakpointIcon('desktop', 'lg')}
                                        {this.breakpointIcon('tablet', 'md')}
                                        {this.breakpointIcon('mobile', 'sm')}
                                    </Col>
                                </Row>
                                <span>{this.state.previewWidth} pixels is a preview width</span>
                                <Row>
                                    <Col>
                                        <div
                                            className="preview-container"
                                            style={
                                                {
                                                    width: this.state.previewWidth,
                                                }
                                            }
                                        >
                                            <div
                                                style={
                                                    {
                                                        display: showPixboostJsPreview
                                                    }
                                                }
                                            >
                                                <ReactIframeResizer
                                                    iframeResizerUrl={false}
                                                    src={`${process.env.PUBLIC_URL}/preview.html`}
                                                    frameBorder={0}
                                                    ref={(iframe: { refs: { frame: HTMLIFrameElement } }) => {
                                                        if (iframe) {
                                                            this.previewFrame = iframe.refs.frame;
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div
                                                style={
                                                    {
                                                        display: showHTMLPreview
                                                    }
                                                }
                                            >
                                                <ReactIframeResizer
                                                    iframeResizerUrl={false}
                                                    src={`${process.env.PUBLIC_URL}/preview-html.html`}
                                                    frameBorder={0}
                                                    ref={(iframe: { refs: { frame: HTMLIFrameElement } }) => {
                                                        if (iframe) {
                                                            this.previewHTMLFrame = iframe.refs.frame;
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <Nav tabs={true} className="pt-4">
                            <NavItem>
                                <NavLink
                                    href={'#'}
                                    className={this.state.activeTab === 'pixboostJs' ? 'active' : ''}
                                    onClick={() => {
                                        this.toggleTab('pixboostJs');
                                    }}
                                >
                                    Pixboost.js
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    href={'#'}
                                    className={this.state.activeTab === 'html' ? 'active js-html-tab' : 'js-html-tab'}
                                    onClick={() => {
                                        this.toggleTab('html');
                                    }}
                                >
                                    HTML
                                </NavLink>
                            </NavItem>
                        </Nav>
                        <TabContent activeTab={this.state.activeTab}>
                            <TabPane tabId={'pixboostJs'}>
                                <PixboostJsSnippet
                                    breakpoints={this.state.breakpoints}
                                    responsive={this.state.responsive}
                                    backgroundImage={this.state.backgroundImage}
                                    lazy={this.state.lazy}
                                    altText={this.state.altText}
                                    onSnippetChange={(s) => this.onSnippetChanged(s)}
                                    apiKey={this.state.apiKey}
                                />
                            </TabPane>
                            <TabPane tabId={'html'}>
                                <HTMLSnippet
                                    breakpoints={this.state.breakpoints}
                                    responsive={this.state.responsive}
                                    backgroundImage={this.state.backgroundImage}
                                    lazy={this.state.lazy}
                                    apiKey={this.state.apiKey}
                                    altText={this.state.altText}
                                    onSnippetChange={(s) => this.onHTMLSnippetChanged(s)}
                                />
                            </TabPane>
                        </TabContent>
                    </div>
                    }
                </Container>
            </div>
        );
    }
}

export default App;
export type TBreakpoint = Breakpoint;
export type TState = State;
// export {Breakpoint, State};
